'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FishPrice {
  id: string;
  name: string;
  price: number;
  currency: string;
  trend: 'up' | 'down' | 'stable';
  changePercent: number;
}

interface PriceTickerBarProps {
  autoScroll?: boolean;
  scrollSpeed?: number;
}

const PriceTickerBar = ({ autoScroll = true, scrollSpeed = 30 }: PriceTickerBarProps) => {
  const [prices, setPrices] = useState<FishPrice[]>([
    { id: '1', name: 'Salmon', price: 850.00, currency: '₹', trend: 'up', changePercent: 2.5 },
    { id: '2', name: 'Tuna', price: 1200.00, currency: '₹', trend: 'down', changePercent: -1.2 },
    { id: '3', name: 'Pomfret', price: 650.00, currency: '₹', trend: 'stable', changePercent: 0 },
    { id: '4', name: 'Prawns', price: 480.00, currency: '₹', trend: 'up', changePercent: 3.8 },
    { id: '5', name: 'Lobster', price: 1800.00, currency: '₹', trend: 'up', changePercent: 5.2 },
    { id: '6', name: 'Crab', price: 750.00, currency: '₹', trend: 'down', changePercent: -0.8 },
    { id: '7', name: 'Mackerel', price: 280.00, currency: '₹', trend: 'stable', changePercent: 0 },
    { id: '8', name: 'Sardines', price: 180.00, currency: '₹', trend: 'up', changePercent: 1.5 },
  ]);

  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoScroll || isPaused) return;

    const interval = setInterval(() => {
      setPrices((prevPrices) => {
        const updatedPrices = [...prevPrices];
        const firstItem = updatedPrices.shift();
        if (firstItem) updatedPrices.push(firstItem);
        return updatedPrices;
      });
    }, scrollSpeed * 100);

    return () => clearInterval(interval);
  }, [autoScroll, isPaused, scrollSpeed]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <Icon name="ArrowTrendingUpIcon" size={14} className="text-success" />;
      case 'down':
        return <Icon name="ArrowTrendingDownIcon" size={14} className="text-error" />;
      default:
        return <Icon name="MinusIcon" size={14} className="text-muted-foreground" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div 
      className="fixed top-[60px] left-0 right-0 z-[90] bg-muted border-b border-border"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="overflow-hidden">
        <div className="flex items-center gap-8 px-6 py-3 animate-fade-in">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground whitespace-nowrap">
            <Icon name="ChartBarIcon" size={18} className="text-primary" />
            <span className="font-heading">Live Prices</span>
          </div>

          <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
            {prices.map((fish) => (
              <div
                key={fish.id}
                className="flex items-center gap-3 px-4 py-2 bg-card rounded-md shadow-elevation-sm transition-smooth hover:shadow-elevation-md hover-lift cursor-pointer whitespace-nowrap"
              >
                <span className="font-medium text-sm text-foreground">{fish.name}</span>
                <span className="font-data font-medium text-sm text-primary">
                  {fish.currency}{fish.price.toFixed(2)}
                </span>
                <div className="flex items-center gap-1">
                  {getTrendIcon(fish.trend)}
                  {fish.changePercent !== 0 && (
                    <span className={`font-data text-xs ${getTrendColor(fish.trend)}`}>
                      {fish.changePercent > 0 ? '+' : ''}{fish.changePercent.toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setIsPaused(!isPaused)}
            className="ml-auto p-2 rounded-md hover:bg-card transition-smooth"
            aria-label={isPaused ? 'Resume ticker' : 'Pause ticker'}
          >
            <Icon name={isPaused ? 'PlayIcon' : 'PauseIcon'} size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceTickerBar;